import { useEffect, useRef, useState } from "react";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import FormError from "../components/salesDocuments/FormError";
import { useCurrentUserFullName } from "../hooks/authHooks";
import { isHttpError } from "../services/apiService";
import {
  EmailGenerationJobStatus,
  EmailLength,
  EmailRelationship,
  EmailTone,
  getEmailGenerationJob,
  startEmailGeneration,
} from "../services/emailGenerator.service";

import {
  CONTACT_NAME_MAX_LENGTH,
  lengthOptions,
  relationshipOptions,
  toneOptions,
} from "./EmailGenerator.constants";

// Generation queues a job and the result is fetched by polling - it can take longer than a
// single request/response allows, especially when a landing page needs to be scraped first.
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 180_000;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

interface EmailGeneratorFormState {
  companyName: string;
  landingPage: string;
  noLandingPage: boolean;
  contactName: string;
  relationship: EmailRelationship;
  recipientEmail: string;
  tone: EmailTone;
  length: EmailLength;
  originalEmail: string;
}

const initialFormState: EmailGeneratorFormState = {
  companyName: "",
  landingPage: "",
  noLandingPage: false,
  contactName: "",
  relationship: "cold",
  recipientEmail: "",
  tone: "Neutral",
  length: "Medium",
  originalEmail: "",
};

// mailto bodies need CRLF line endings for mail clients to render line breaks correctly -
// encodeURIComponent then takes care of every other character (&, %, +, etc) that would
// otherwise corrupt the mailto URL's query string.
const toMailtoBody = (body: string): string =>
  encodeURIComponent(body.replace(/\r\n/g, "\n").replace(/\n/g, "\r\n"));

// The model is instructed not to invent a sign-off name (see EmailPrompt), so the generated
// content ends after the closing line (e.g. "Best regards,") with nothing after it - the
// signed-in user's real name is appended here rather than trusting the AI to know it.
const appendSignOff = (content: string, fullName: string): string =>
  fullName ? `${content.trimEnd()}\n${fullName}` : content;

const EmailGenerator = () => {
  const fullName = useCurrentUserFullName();
  const [form, setForm] = useState<EmailGeneratorFormState>(initialFormState);
  const [isGenerating, setIsGenerating] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [hasResult, setHasResult] = useState(false);
  const [editableSubject, setEditableSubject] = useState("");
  const [editableBody, setEditableBody] = useState("");
  const [copyConfirmation, setCopyConfirmation] = useState(false);

  // Reset on every mount (not just declared once) so React.StrictMode's dev-only
  // mount -> cleanup -> mount cycle doesn't leave this stuck at true forever.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const updateField = <K extends keyof EmailGeneratorFormState>(
    field: K,
    value: EmailGeneratorFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setRequestError(null);
  };

  const handleReset = () => {
    setForm(initialFormState);
    setHasResult(false);
    setEditableSubject("");
    setEditableBody("");
    setRequestError(null);
    setCopyConfirmation(false);
  };

  const pollForResult = async (
    jobId: string,
  ): Promise<Exclude<EmailGenerationJobStatus, { status: "PENDING" }>> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    for (;;) {
      const job = await getEmailGenerationJob(jobId);
      if (job.status !== "PENDING") return job;
      if (Date.now() >= deadline || cancelledRef.current) {
        throw new Error("Email generation timed out. Please try again.");
      }
      await wait(POLL_INTERVAL_MS);
    }
  };

  const describeError = (error: unknown): string => {
    if (!isHttpError(error)) {
      return error instanceof Error ? error.message : "Unable to generate email. Please try again.";
    }

    switch (error.response.status) {
      case 401:
        return "Your session has expired. Please sign in again.";
      case 403:
        return "You don't have permission to use the Email Generator.";
      case 400:
        return "Unable to generate email - please check the form for invalid fields.";
      default:
        return `Unable to generate email (HTTP ${String(error.response.status)}).`;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setRequestError(null);
    setHasResult(false);
    setIsGenerating(true);

    try {
      const { job_id: jobId } = await startEmailGeneration({
        tone: form.tone,
        length: form.length,
        email: form.originalEmail,
        recipient_email: form.recipientEmail,
        relationship: form.relationship,
        ...(form.companyName.trim() && { company_name: form.companyName.trim() }),
        ...(!form.noLandingPage &&
          form.landingPage.trim() && { landing_page: form.landingPage.trim() }),
        ...(form.contactName.trim() && { contact_name: form.contactName.trim() }),
      });

      const job = await pollForResult(jobId);

      if (job.status === "FAILED") {
        setRequestError(job.error);
        return;
      }

      setEditableSubject(job.subject);
      setEditableBody(appendSignOff(job.content, fullName));
      setHasResult(true);
    } catch (error) {
      setRequestError(describeError(error));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editableBody);
      setCopyConfirmation(true);
      setTimeout(() => {
        setCopyConfirmation(false);
      }, 2000);
    } catch {
      setRequestError("Failed to copy email to clipboard.");
    }
  };

  const mailtoHref = `mailto:${encodeURIComponent(form.recipientEmail)}?subject=${encodeURIComponent(
    editableSubject,
  )}&body=${toMailtoBody(editableBody)}`;

  return (
    <div>
      <h1>Email Generator</h1>

      <div className="meter-search-container">
        <form onSubmit={(event) => void handleSubmit(event)}>
          <fieldset className="docs-form__section" disabled={isGenerating}>
            <div className="docs-form__customer-field">
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-company-name">
                    Company name <small>(optional)</small>
                  </label>
                  <input
                    id="email-generator-company-name"
                    type="text"
                    value={form.companyName}
                    onChange={(event) => {
                      updateField("companyName", event.target.value);
                    }}
                    placeholder="Example: Lee's Custom Woodwork"
                  />
                </div>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-landing-page">Landing page URL</label>
                  <input
                    id="email-generator-landing-page"
                    type="url"
                    value={form.landingPage}
                    onChange={(event) => {
                      updateField("landingPage", event.target.value);
                    }}
                    placeholder="Example: https://leescustomwoodwork.com"
                    required={!form.noLandingPage}
                    disabled={form.noLandingPage}
                  />
                  <label
                    htmlFor="email-generator-no-landing-page"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginTop: "0.75rem",
                      fontWeight: 400,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      id="email-generator-no-landing-page"
                      type="checkbox"
                      checked={form.noLandingPage}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          noLandingPage: checked,
                          landingPage: checked ? "" : prev.landingPage,
                        }));
                        setRequestError(null);
                      }}
                    />
                    No landing page available
                  </label>
                </div>
              </div>
            </div>

            <div className="docs-form__customer-field">
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-contact-name">
                    Contact name <small>(optional)</small>
                  </label>
                  <input
                    id="email-generator-contact-name"
                    type="text"
                    value={form.contactName}
                    onChange={(event) => {
                      updateField("contactName", event.target.value);
                    }}
                    placeholder="Example: Lee Smith"
                    maxLength={CONTACT_NAME_MAX_LENGTH}
                  />
                </div>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-recipient-email">
                    Recipient email <small>(optional)</small>
                  </label>
                  <input
                    id="email-generator-recipient-email"
                    type="email"
                    value={form.recipientEmail}
                    onChange={(event) => {
                      updateField("recipientEmail", event.target.value);
                    }}
                    placeholder="recipient@company.com"
                  />
                </div>
              </div>
            </div>

            <div className="docs-form__customer-field">
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-tone">Tone</label>
                  <select
                    id="email-generator-tone"
                    value={form.tone}
                    onChange={(event) => {
                      updateField("tone", event.target.value as EmailTone);
                    }}
                  >
                    {toneOptions.map((tone) => (
                      <option key={tone} value={tone}>
                        {tone}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="docs-form__inputs" style={{ flex: 1 }}>
                  <label htmlFor="email-generator-length">Email length</label>
                  <select
                    id="email-generator-length"
                    value={form.length}
                    onChange={(event) => {
                      updateField("length", event.target.value as EmailLength);
                    }}
                  >
                    {lengthOptions.map((length) => (
                      <option key={length} value={length}>
                        {length}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="docs-form__customer-field">
              <div className="docs-form__inputs">
                <label htmlFor="email-generator-relationship">Relationship type</label>
                <select
                  id="email-generator-relationship"
                  value={form.relationship}
                  onChange={(event) => {
                    updateField("relationship", event.target.value as EmailRelationship);
                  }}
                >
                  {relationshipOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="docs-form__customer-field">
              <div className="docs-form__inputs">
                <label htmlFor="email-generator-original-email">Your email content</label>
                <textarea
                  id="email-generator-original-email"
                  rows={5}
                  value={form.originalEmail}
                  onChange={(event) => {
                    updateField("originalEmail", event.target.value);
                  }}
                  placeholder="Write the key points you want the email to cover..."
                  required
                  style={{ maxWidth: "100%" }}
                />
              </div>
            </div>

            {requestError && (
              <div className="docs-form__error-messages">
                <FormError text={requestError} />
              </div>
            )}

            <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
              <Button type="submit">Generate Email</Button>
              <Button type="button" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </fieldset>
        </form>

        {hasResult && (
          <div className="meter">
            <div className="meter__title">Generated Email</div>

            <div className="docs-form__customer-field">
              <div className="docs-form__inputs">
                <label htmlFor="email-generator-subject">Subject</label>
                <input
                  id="email-generator-subject"
                  type="text"
                  value={editableSubject}
                  onChange={(event) => {
                    setEditableSubject(event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="docs-form__customer-field">
              <div className="docs-form__inputs">
                <label htmlFor="email-generator-body">Email content</label>
                <textarea
                  id="email-generator-body"
                  rows={12}
                  value={editableBody}
                  onChange={(event) => {
                    setEditableBody(event.target.value);
                  }}
                  style={{ whiteSpace: "pre-wrap", maxWidth: "100%" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <Button type="button" onClick={() => void handleCopyToClipboard()}>
                {copyConfirmation ? "Copied!" : "Copy to Clipboard"}
              </Button>
              <a className="btn" style={{ textDecoration: "none" }} href={mailtoHref}>
                Send to Outlook
              </a>
            </div>
          </div>
        )}
      </div>

      {isGenerating && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "2rem 2.5rem",
              maxWidth: "min(420px, calc(100vw - 2rem))",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
              textAlign: "center",
            }}
          >
            <LoadingSpinner
              message="Most queries return within a few seconds, but it may take up to 3 minutes."
              size="medium"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailGenerator;
